import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePostHog } from "posthog-js/react";
import { useDispatch, useSelector } from "react-redux";
import { InsightsReportScheduleTypeEnum, InsightsScheduleDestinationEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert } from "~/components-shared";
import moment from "moment";
import InsightsReportForm from "~/forms/InsightsReportForm";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import { useHistory } from "react-router-dom";
import insightsReportSchema from "~/forms/InsightsReportForm/insightsReportSchema";
const Sticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const InsightsReportCreateModal = () => {
  const {
    t
  } = useTranslation();
  const action = useSelector(insightsSelectors.getAction);
  const filter = useSelector(insightsSelectors.getFilter);
  const view = useSelector(insightsSelectors.getView);
  const status = useSelector(insightsSelectors.getStatusByConfig("+"));
  const error = useSelector(insightsSelectors.getErrorByConfig("+"));
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const history = useHistory();
  const defaultValues = {
    name: "",
    topicIds: filter.topicIds,
    config: view?.filter(_ref => {
      let {
        enabled
      } = _ref;
      return enabled;
    }).map(_ref2 => {
      let {
        type,
        size
      } = _ref2;
      return {
        type,
        size
      };
    }),
    scheduleType: InsightsReportScheduleTypeEnum.WEEKLY,
    scheduleDayOfWeek: 4,
    scheduleTimeOfDay: moment("10:00:00", "hh:mm:ss").toISOString(),
    scheduleDestination: InsightsScheduleDestinationEnum.SLACK
  };
  const {
    handleSubmit,
    control,
    reset,
    watch
  } = useForm({
    resolver: yupResolver(insightsReportSchema),
    defaultValues
  });
  useEffect(() => {
    if (!action) {
      reset(defaultValues);
    }
  }, [action]);
  const onSubmit = handleSubmit(data => {
    dispatch(insightsActionCreators.createConfigStarted(data, _ref3 => {
      let {
        data: config
      } = _ref3;
      if (config) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("InsightsReportCreateModal.reportCreated", {
            name: config.name
          }),
          timeout: 10000
        }));
        reset(defaultValues);
        history.push(RoutesEnum.INSIGHTS_CONFIGS);
      }
    }));
    posthog.capture(`Insights Report Create – Save`, {
      ...data
    });
  });
  const onCancel = () => {
    dispatch(insightsActionCreators.actionChanged(null, null));
    posthog.capture(`Insights Report Create – Cancel`);
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("InsightsReportCreateModal.createReport"),
    onClose: onCancel,
    maxWidth: "640px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement(InsightsReportForm, {
    control: control,
    watch: watch
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: onCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.create")))));
};
export default InsightsReportCreateModal;